/** Flow 4 — Planification hebdomadaire (simulation, no backend) */

export const FLOW4_TITLE = "Flow 4 — Planification hebdomadaire";

export const FLOW4_STEPS = [
  "Rappel des objectifs à traiter",
  "Disponibilités de l'élève",
  "Génération du plan de la semaine",
  "Approbation ou modification",
  "Plan final activé",
];

export const FLOW4_GOALS = [
  ["Maths", "Réviser les fonctions"],
  ["Français", "Préparer la dissertation"],
  ["Sciences", "Revoir le chapitre 3"],
  ["Histoire", "Apprendre la frise chronologique"],
];

export const DAY_OPTIONS = [
  { id: "lun", label: "Lundi", slot: "17h – 19h" },
  { id: "mar", label: "Mardi", slot: "16h – 18h" },
  { id: "mer", label: "Mercredi", slot: "Pas disponible" },
  { id: "jeu", label: "Jeudi", slot: "17h – 20h" },
  { id: "ven", label: "Vendredi", slot: "16h – 18h" },
  { id: "sam", label: "Samedi", slot: "10h – 12h" },
  { id: "dim", label: "Dimanche", slot: "Pas disponible" },
];

export const CANON_DAYS = ["Lundi", "Mardi", "Jeudi", "Vendredi", "Samedi"];

export const FLOW4_CHECKS = [
  "Analyse des objectifs",
  "Répartition des matières",
  "Adaptation à tes créneaux",
  "Création du plan",
];

export const WEEK_PLAN = [
  ["Lun 14 oct.", "Maths – Fonctions", "17h00 – 18h00", "1 h"],
  ["Mar 15 oct.", "Français – Dissertation", "16h00 – 17h30", "1 h 30"],
  ["Mer 16 oct.", "Aucune séance", "Jour de repos", "—"],
  ["Jeu 17 oct.", "Sciences – Chapitre 3", "17h00 – 18h30", "1 h 30"],
  ["Ven 18 oct.", "Histoire – Frise chrono", "16h00 – 17h00", "1 h"],
  ["Sam 19 oct.", "Maths – Exercices", "10h00 – 11h30", "1 h 30"],
];

export const WEEK_PLAN_SHIFTED = WEEK_PLAN.map((row) =>
  row[0].startsWith("Sam") ? ["Sam 19 oct.", "Maths – Exercices", "9h00 – 10h30", "1 h 30"] : row
);

export const PLAN_SUMMARY = [
  ["Matières", "4 matières"],
  ["Séances", "6 séances"],
  ["Durée", "Environ 5 h 30 d'étude"],
];

export const READY_STATS = [
  "6 séances planifiées",
  "5 h 30 d'étude",
  "4 matières concernées",
  "Adapté à tes disponibilités",
];

export const GOALS_MESSAGE =
  "Voici les objectifs que tu as choisis. Je vais les intégrer dans ton plan de la semaine.";

export const CONFIRM_MESSAGE =
  "Souhaites-tu planifier ces objectifs pour la semaine à venir ?";

export const AVAIL_MESSAGE =
  "Parfait ! Dis-moi quand tu es disponible pour étudier cette semaine. Tu peux sélectionner tes créneaux, ou me dire tes habitudes.";

export const GENERATE_MESSAGE =
  "Super ! Je génère ton plan d'étude en fonction de tes objectifs et de tes disponibilités.";

export const PREVIEW_MESSAGE = "Voici ton plan pour la semaine prochaine !";

export const REVIEW_MESSAGE =
  "Voici ton plan de la semaine prochaine. Qu'en penses-tu ?";

export const CHOICE_MESSAGE =
  "Tu souhaites le garder ainsi ou modifier quelque chose ?";

export const MODIFY_MESSAGE =
  "Dis-moi ce que tu veux ajuster : un horaire, une matière ou une durée.";

export const MODIFIED_MESSAGE = "C'est noté. Le samedi commence à 9 h.";

export const ACTIVE_MESSAGE =
  "C'est prêt ! Ton plan d'étude est activé pour la semaine prochaine.";

export const COURAGE_MESSAGE =
  "Bon courage ! Tu vas faire de beaux progrès cette semaine !";

export const SAVED_MESSAGE =
  "Voici ton plan : 6 séances, 5 h 30 d'étude, sur 4 matières, adapté à tes disponibilités.";

export function emptyWeek() {
  return { days: [], modified: false };
}

export function flow4DisplayIndex(flowStep) {
  if (flowStep <= 0) return 0;
  if (flowStep === 1) return 1;
  if (flowStep === 2) return 2;
  if (flowStep <= 5) return 3;
  return 4;
}

export function orderedDays(labels) {
  const picked = labels.length ? labels : CANON_DAYS;
  return DAY_OPTIONS.map((day) => day.label).filter((label) => picked.includes(label));
}

export function daysSentence(labels) {
  const picked = orderedDays(labels);
  if (picked.length === 1) return `${picked[0]}.`;
  return `${picked.slice(0, -1).join(", ")} et ${picked[picked.length - 1]}.`;
}

export function dayReplies(selected = []) {
  return [
    ...DAY_OPTIONS.map((day) => ({
      id: `f4-day-${day.id}`,
      label: `${day.label} · ${day.slot}`,
      primary: false,
      selected: selected.includes(day.label),
    })),
    { id: "f4-days-ok", label: "Continuer", primary: true },
  ];
}

export function labelForDay(id) {
  return DAY_OPTIONS.find((day) => `f4-day-${day.id}` === id)?.label || "";
}

export const FLOW4_REPLIES = {
  go: [{ id: "f4-go", label: "Oui, on y va !", primary: true }],
  see: [{ id: "f4-see", label: "Voir le plan", primary: true }],
  review: [
    { id: "f4-ok", label: "Valider et partager", primary: true },
    { id: "f4-edit", label: "Modifier", primary: false },
  ],
  shift: [{ id: "f4-shift", label: "Avancer le samedi à 9 h", primary: true }],
  bridge: [{ id: "f4-start", label: "Planifier la semaine", primary: true }],
};

function result(messages, quickReplies, flowStep, planReady = false) {
  return {
    messages,
    quickReplies,
    flowStep,
    status: FLOW4_STEPS[flow4DisplayIndex(flowStep)],
    planReady,
    week: { days: flowStep >= 1 ? [...CANON_DAYS] : [], modified: false },
  };
}

/** Instant history for a sommaire step (display index 0–4). */
export function buildFlow4Snapshot(displayIndex) {
  const step = Math.min(Math.max(displayIndex, 0), FLOW4_STEPS.length - 1);
  const messages = [
    { sender: "agent", messageKey: "f4.goals", text: GOALS_MESSAGE },
    { sender: "agent", kind: "goals" },
    { sender: "agent", messageKey: "f4.confirm", text: CONFIRM_MESSAGE },
  ];
  if (step === 0) return result(messages, FLOW4_REPLIES.go, 0);

  messages.push({ sender: "user", text: "Oui, on y va !" });
  messages.push({ sender: "agent", messageKey: "f4.avail", text: AVAIL_MESSAGE });
  if (step === 1) return result(messages, dayReplies(CANON_DAYS), 1);

  messages.push({ sender: "user", text: daysSentence(CANON_DAYS) });
  messages.push({ sender: "agent", messageKey: "f4.generate", text: GENERATE_MESSAGE });
  messages.push({ sender: "agent", kind: "build" });
  messages.push({ sender: "agent", messageKey: "f4.preview", text: PREVIEW_MESSAGE });
  if (step === 2) return result(messages, FLOW4_REPLIES.see, 2, true);

  messages.push({ sender: "user", text: "Voir le plan" });
  messages.push({ sender: "agent", messageKey: "f4.review", text: REVIEW_MESSAGE });
  messages.push({ sender: "agent", kind: "weekplan" });
  messages.push({ sender: "agent", messageKey: "f4.choice", text: CHOICE_MESSAGE });
  if (step === 3) return result(messages, FLOW4_REPLIES.review, 3, true);

  messages.push({ sender: "user", text: "Valider et partager" });
  messages.push({ sender: "agent", messageKey: "f4.active", text: ACTIVE_MESSAGE });
  messages.push({ sender: "agent", kind: "readyplan" });
  messages.push({ sender: "agent", messageKey: "f4.courage", text: COURAGE_MESSAGE });
  return result(messages, [], 6, true);
}
